// Meta Pixel + CAPI tracking utilities with deduplication
// Implements Meta Parameter Builder best practices

import { supabase } from "@/integrations/supabase/client";

declare global {
  interface Window {
    fbq: (track: string, event: string, params?: any, options?: any) => void;
  }
}

// Generate unique event ID for deduplication
export const generateEventId = (): string => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create a persistent user ID for tracking
const getExternalId = (): string => {
  let externalId = localStorage.getItem("meta_external_id");
  if (!externalId) {
    externalId = `ext_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
    localStorage.setItem("meta_external_id", externalId);
  }
  return externalId;
};

// Get fbp (Facebook browser ID) from cookie - DO NOT modify or normalize
const getFbp = (): string | null => {
  const match = document.cookie.match(/_fbp=([^;]+)/);
  return match ? match[1] : null;
};

// Get fbc (Facebook click ID) from cookie or URL - case sensitive, do not normalize
const getFbc = (): string | null => {
  // First check cookie - DO NOT lowercase or modify
  const cookieMatch = document.cookie.match(/_fbc=([^;]+)/);
  if (cookieMatch) return cookieMatch[1];
  
  // If not in cookie, construct from fbclid URL param
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get("fbclid");
  if (fbclid) {
    // Format: fb.{subdomain_index}.{creation_time}.{fbclid}
    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    // Store for future use - set long expiry for better attribution
    document.cookie = `_fbc=${fbc}; max-age=${90 * 24 * 60 * 60}; path=/; SameSite=Lax`;
    return fbc;
  }
  
  return null;
};

// Get client IP from _fbi cookie (set by initializeMetaParams)
const getClientIpFromCookie = (): string | null => {
  const match = document.cookie.match(/_fbi=([^;]+)/);
  return match ? match[1] : null;
};

// Initialize Meta parameters early - call this on page load
// This follows Meta's best practice of capturing fbc/fbp/ip as early as possible
export const initializeMetaParams = async (): Promise<void> => {
  try {
    // Capture fbc from URL immediately if present
    getFbc();
    
    // Fetch client IP (including IPv6) from edge function and store in cookie
    const { data, error } = await supabase.functions.invoke("get-client-ip");
    
    if (!error && data?.ip) {
      // Store IP in _fbi cookie for later retrieval (Meta Parameter Builder convention)
      const maxAge = 90 * 24 * 60 * 60; // 90 days
      document.cookie = `_fbi=${data.ip}; max-age=${maxAge}; path=/; SameSite=Lax`;
      console.log(`[Meta Params] Stored client IP: ${data.ip} (${data.version})`);
    }
  } catch (error) {
    console.error("[Meta Params] Failed to initialize:", error);
  }
};

// Store and retrieve UTM parameters
export const storeUtmParams = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};
  
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid"].forEach(param => {
    const value = urlParams.get(param);
    if (value) {
      utmParams[param] = value;
    }
  });
  
  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem("utm_params", JSON.stringify(utmParams));
  }
};

export const getUtmParams = (): Record<string, string> => {
  const stored = sessionStorage.getItem("utm_params");
  return stored ? JSON.parse(stored) : {};
};

// SHA256 hash function for Advanced Matching
const sha256Hash = async (value: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(value.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
};

// User data interface for Advanced Matching
export interface UserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  gender?: 'm' | 'f'; // ge parameter - hashing required
  dateOfBirth?: string; // db parameter - format YYYYMMDD - hashing required
}

// Product data interface for detailed tracking
export interface ProductData {
  id: string;
  name: string;
  price: number;
  currency?: string;
  quantity?: number;
  weight?: number; // in grams
}

// Store user data for Advanced Matching
export const storeUserData = (userData: UserData): void => {
  sessionStorage.setItem("meta_user_data", JSON.stringify(userData));
};

export const getUserData = (): UserData => {
  const stored = sessionStorage.getItem("meta_user_data");
  return stored ? JSON.parse(stored) : {};
};

// Hash user data for Advanced Matching
const hashUserData = async (userData: UserData): Promise<Record<string, string>> => {
  const hashed: Record<string, string> = {};
  
  if (userData.email) {
    hashed.em = await sha256Hash(userData.email);
  }
  if (userData.phone) {
    // Remove all non-numeric characters and hash
    const cleanPhone = userData.phone.replace(/\D/g, "");
    hashed.ph = await sha256Hash(cleanPhone);
  }
  if (userData.firstName) {
    hashed.fn = await sha256Hash(userData.firstName);
  }
  if (userData.lastName) {
    hashed.ln = await sha256Hash(userData.lastName);
  }
  if (userData.city) {
    hashed.ct = await sha256Hash(userData.city);
  }
  if (userData.state) {
    hashed.st = await sha256Hash(userData.state);
  }
  if (userData.zipCode) {
    hashed.zp = await sha256Hash(userData.zipCode);
  }
  if (userData.country) {
    hashed.country = await sha256Hash(userData.country);
  }
  if (userData.gender) {
    hashed.ge = await sha256Hash(userData.gender);
  }
  if (userData.dateOfBirth) {
    // Format should be YYYYMMDD
    hashed.db = await sha256Hash(userData.dateOfBirth.replace(/\D/g, ""));
  }
  
  return hashed;
};

// Send event to CAPI via edge function
const sendToCapiEdge = async (
  eventName: string,
  eventId: string,
  eventData: Record<string, any>,
  userData: UserData = {}
): Promise<void> => {
  try {
    const hashedUserData = await hashUserData(userData);
    const fbp = getFbp();
    const fbc = getFbc();
    const externalId = getExternalId();
    const utmParams = getUtmParams();
    const clientIpFromCookie = getClientIpFromCookie();
    
    const payload = {
      eventName,
      eventId,
      eventTime: Math.floor(Date.now() / 1000),
      eventSourceUrl: window.location.href,
      referrerUrl: document.referrer || null, // Required by Meta CAPI
      userData: {
        ...hashedUserData,
        external_id: await sha256Hash(externalId),
        fbp,
        fbc,
        // Send client IP from cookie if available (captured via get-client-ip edge function)
        // Edge function will use this or fall back to request headers
        client_ip_address: clientIpFromCookie,
        client_user_agent: navigator.userAgent,
      },
      customData: {
        ...eventData,
        ...utmParams,
      },
      // Data processing options for CCPA/GDPR compliance (empty array = no restrictions)
      dataProcessingOptions: [],
      dataProcessingOptionsCountry: 0,
      dataProcessingOptionsState: 0,
    };
    
    console.log(`[Meta CAPI] Sending ${eventName}:`, {
      event_id: eventId,
      fbp,
      fbc,
      client_ip: clientIpFromCookie ? "from_cookie" : "from_headers",
      external_id: externalId,
      has_phone: !!hashedUserData.ph,
      value: eventData.value,
      currency: eventData.currency,
      content_ids: eventData.content_ids,
      num_items: eventData.num_items,
      contents: eventData.contents,
    });
    
    await supabase.functions.invoke("meta-capi", { body: payload });
    
    console.log(`[Meta CAPI] ${eventName} sent successfully with event_id: ${eventId}`);
  } catch (error) {
    console.error(`[Meta CAPI] Error sending ${eventName}:`, error);
  }
};

// Track PageView
export const trackPageView = (eventId?: string): void => {
  const id = eventId || generateEventId();
  
  // Browser Pixel
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "PageView", {}, { eventID: id });
    console.log(`[Meta Pixel] PageView tracked with event_id: ${id}`);
  }
  
  // Server CAPI
  sendToCapiEdge("PageView", id, {}, getUserData());
};

// Track ViewContent
export const trackViewContent = (
  contentName: string,
  contentId: string,
  value?: number,
  currency: string = "USD"
): void => {
  const eventId = generateEventId();
  const eventData = {
    content_name: contentName,
    content_ids: [contentId],
    content_type: "product",
    value: value || 0,
    currency,
  };
  
  // Browser Pixel
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "ViewContent", eventData, { eventID: eventId });
    console.log(`[Meta Pixel] ViewContent tracked: ${contentName}`);
  }
  
  // Server CAPI
  sendToCapiEdge("ViewContent", eventId, eventData, getUserData());
};

// Track AddToCart
export const trackAddToCart = (
  productName: string,
  price: string,
  priceId: string,
  quantity: number = 1,
  weightInGrams: number = 60 // Default weight for supplement bottle
): void => {
  const eventId = generateEventId();
  const numericPrice = parseFloat(price.replace(/[^0-9.]/g, ""));
  const eventData = {
    content_name: productName,
    content_ids: [priceId],
    content_type: "product",
    value: numericPrice,
    currency: "USD",
    num_items: quantity,
    contents: [{
      id: priceId,
      quantity: quantity,
      item_price: numericPrice / quantity,
      delivery_category: "home_delivery",
    }],
  };
  
  // Browser Pixel
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "AddToCart", eventData, { eventID: eventId });
    console.log(`[Meta Pixel] AddToCart tracked: ${productName}, qty: ${quantity}`);
  }
  
  // Server CAPI
  sendToCapiEdge("AddToCart", eventId, eventData, getUserData());
};

// Track InitiateCheckout
export const trackInitiateCheckout = (
  productName: string,
  price: string,
  priceId: string,
  quantity: number = 1
): void => {
  const eventId = generateEventId();
  const numericPrice = parseFloat(price.replace(/[^0-9.]/g, ""));
  const eventData = {
    content_name: productName,
    content_ids: [priceId],
    content_type: "product",
    value: numericPrice,
    currency: "USD",
    num_items: quantity,
    contents: [{
      id: priceId,
      quantity: quantity,
      item_price: numericPrice / quantity,
      delivery_category: "home_delivery",
    }],
  };
  
  // Browser Pixel
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "InitiateCheckout", eventData, { eventID: eventId });
    console.log(`[Meta Pixel] InitiateCheckout tracked: ${productName}, qty: ${quantity}`);
  }
  
  // Server CAPI
  sendToCapiEdge("InitiateCheckout", eventId, eventData, getUserData());
};

// Track AddPaymentInfo
export const trackAddPaymentInfo = (
  productName: string,
  price: string,
  priceId: string,
  quantity: number = 1
): void => {
  const eventId = generateEventId();
  const numericPrice = parseFloat(price.replace(/[^0-9.]/g, ""));
  const eventData = {
    content_name: productName,
    content_ids: [priceId],
    content_type: "product",
    value: numericPrice,
    currency: "USD",
    num_items: quantity,
    contents: [{
      id: priceId,
      quantity: quantity,
      item_price: numericPrice / quantity,
      delivery_category: "home_delivery",
    }],
  };
  
  // Browser Pixel
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "AddPaymentInfo", eventData, { eventID: eventId });
    console.log(`[Meta Pixel] AddPaymentInfo tracked: ${productName}, qty: ${quantity}`);
  }
  
  // Server CAPI
  sendToCapiEdge("AddPaymentInfo", eventId, eventData, getUserData());
};

// Track Purchase - CRITICAL: Must fire instantly with correct value
export const trackPurchase = (
  productName: string,
  price: string,
  priceId: string,
  userData?: UserData,
  quantity: number = 1
): void => {
  const eventId = generateEventId();
  const numericPrice = parseFloat(price.replace(/[^0-9.]/g, ""));
  const eventData = {
    content_name: productName,
    content_ids: [priceId],
    content_type: "product",
    value: numericPrice,
    currency: "USD",
    num_items: quantity,
    contents: [{
      id: priceId,
      quantity: quantity,
      item_price: numericPrice / quantity,
      delivery_category: "home_delivery",
    }],
  };
  
  // Merge stored user data with provided user data
  const mergedUserData = { ...getUserData(), ...userData };
  
  // Browser Pixel - fires immediately
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "Purchase", eventData, { eventID: eventId });
    console.log(`[Meta Pixel] Purchase tracked: ${productName}, value: ${numericPrice}, qty: ${quantity}, event_id: ${eventId}`);
  }
  
  // Server CAPI - fires immediately (no await to prevent blocking)
  sendToCapiEdge("Purchase", eventId, eventData, mergedUserData);
};

// Track Lead
export const trackLead = (userData?: UserData): void => {
  const eventId = generateEventId();
  const eventData = {
    content_name: "Email Capture",
  };
  
  // Browser Pixel
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", "Lead", eventData, { eventID: eventId });
    console.log(`[Meta Pixel] Lead tracked`);
  }
  
  // Server CAPI
  sendToCapiEdge("Lead", eventId, eventData, userData || getUserData());
};

// Initialize UTM storage on page load
if (typeof window !== "undefined") {
  storeUtmParams();
}
